import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { IS_USE_CENTIMETERS_SIZE_COUNTRY } from '@/constants/is-use-centimeters-size-country';
import { cn } from '@/lib/utils';
import { Tooltip } from 'antd';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ProductRoot } from '../types/product';

export const CustomizedSize = ({
  shippingType,
  product,
  className,
  isCustomizedDress = false,
}: {
  shippingType: 'Dresses' | 'Wedding Dresses';
  product: ProductRoot;
  className?: string;
  isCustomizedDress?: boolean;
}) => {
  const form = useFormContext();
  const { watch, trigger, getValues } = form;
  const selectedSize = watch('Size');
  const isFloorLengthSizeDress =
    product.show_attribute?.find(item => item.code === 'aw_dresses_hemline')?.value === 'Floor Length';
  const aw_dresses_hemline_label = product?.show_attribute.find(item => item.code === 'aw_dresses_hemline')?.value as
    | 'Ankle Length'
    | 'Floor Length'
    | 'Hi Lo'
    | 'Knee Length'
    | 'Mini with Overskirt'
    | 'Mini/Short'
    | 'Tea Length';

  const imageSrc = useMemo(() => {
    if (isCustomizedDress) return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/floor-length.svg`;

    if (shippingType !== 'Dresses') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/size-chart/measuring-guide-1.svg`;
    }

    if (aw_dresses_hemline_label === 'Ankle Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/ankle-length.svg`;
    }

    if (aw_dresses_hemline_label === 'Floor Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/floor-length.svg`;
    }

    if (aw_dresses_hemline_label === 'Hi Lo') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/hi-lo.svg`;
    }

    if (aw_dresses_hemline_label === 'Knee Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/knee-length.svg`;
    }

    if (aw_dresses_hemline_label === 'Mini/Short' || aw_dresses_hemline_label === 'Mini with Overskirt') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/mini-short.svg`;
    }

    if (aw_dresses_hemline_label === 'Tea Length') {
      return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/tea-length.svg`;
    }

    return `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/knee-length.svg`;
  }, [aw_dresses_hemline_label, shippingType, isCustomizedDress]);

  return (
    selectedSize?.label === 'customized_size' && (
      <div className={cn('mt-10 flex flex-col-reverse items-center lg_flex-row lg_items-start', className)}>
        <div className='flex flex-col gap-[16px]'>
          <div className='space-y-4 '>
            {[
              {
                name: 'bust',
                label: 'Bust:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '76-173 cm' : '30-68 inch',
              },
              {
                name: 'waist',
                label: 'Waist:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '56-178 cm' : '22-70 inch',
              },
              {
                name: 'hip',
                label: 'Hip:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '76-191 cm' : '30-75 inch',
              },
              {
                name: shippingType === 'Dresses' ? 'hollowtoHem' : 'hollowToFloor',
                label: shippingType === 'Dresses' ? 'Hollow to Hem:' : 'Hollow to Floor:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '76-178 cm' : '30-70 inch',
              },
              {
                name: 'height',
                label: 'Height:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '127-229 cm' : '50-90 inch',
              },
              {
                name: 'extraLength',
                label: 'Extra Length:',
                hint: IS_USE_CENTIMETERS_SIZE_COUNTRY ? '0-15 cm' : '0-6 inch',
              },
            ].map(field => {
              if (!isFloorLengthSizeDress && field.name === 'extraLength') return null;

              return (
                <FormField
                  key={field.name}
                  control={form.control}
                  name={field.name}
                  render={({ field: inputField }) => (
                    <div>
                      <FormItem className='flex items-center'>
                        <FormLabel className=' flex w-[130px] items-center justify-start gap-1 px-0.5 pt-2.5  text-sm leading-none text-primary lg_text-sm'>
                          {field.label}
                        </FormLabel>
                        <FormControl>
                          <input
                            {...inputField}
                            onChange={e => {
                              const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                              const singleDotValue = numericValue.replace(/(\..*)\./g, '$1');
                              const formattedValue = singleDotValue.replace(/^(\d*\.\d{2}).*$/, '$1');

                              inputField.onChange(formattedValue);
                            }}
                            onBlur={() => {
                              if (
                                (field.name === 'height' || field.name === 'hollowtoHem') &&
                                getValues('height') &&
                                getValues('hollowtoHem')
                              ) {
                                trigger('height');
                                trigger('hollowtoHem');
                              }
                            }}
                            className='border-input text-colorDefault disabled_text-subtle m-0 inline-flex h-[34px] w-[100px] max-w-full border-1 border-solid border-E7DED5 bg-white px-1  outline-none '
                          />
                        </FormControl>
                        <div className='text-gray-500 ml-[5px] flex items-center text-sm'>
                          {field.hint}{' '}
                          {field.label === 'Extra Length:' && (
                            <Tooltip
                              placement='right'
                              title={
                                'Extra length is used for floor-length dresses when you need extra length to fit your shoe heel. If you do not need it, just fill 0.'
                              }
                            >
                              <div className='ml-[5px] flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-[#e7ded5] text-primary'>
                                ?
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      </FormItem>
                      <FormMessage id={field.name} />
                    </div>
                  )}
                />
              );
            })}
          </div>

          {shippingType === 'Dresses' && (
            <span className='flex text-sm leading-none text-primary lg_text-sm'>* Measure in undergarments only.</span>
          )}
        </div>

        <div className='w-full lg_mx-auto lg_max-w-[210px]'>
          <img
            className='mx-auto max-w-[40%] lg_w-auto lg_max-w-[210px]'
            src={imageSrc}
            alt=''
          />
          {aw_dresses_hemline_label === 'Ankle Length' ||
          aw_dresses_hemline_label === 'Floor Length' ||
          aw_dresses_hemline_label === 'Knee Length' ||
          aw_dresses_hemline_label === 'Mini/Short' ||
          aw_dresses_hemline_label === 'Tea Length' ? (
            <div className='mx-auto mt-2.5 text-center text-[13px] text-80583F lg_max-w-[133px]'>
              Measure the length of the dress you desire.
            </div>
          ) : null}
          {aw_dresses_hemline_label === 'Hi Lo' ? (
            <div className='mx-auto mt-2.5 text-center text-[13px] text-80583F lg_max-w-[133px]'>
              Please provide the back length measurement of the dress.
            </div>
          ) : null}
        </div>
      </div>
    )
  );
};
